import { Travel } from "@gen/travel/v1/common_pb";
import { Button, Modal, Skeleton } from "@thetreep/ui";
import { formatPrice } from "@utils/formatPrice";
import { CompactJourney } from "./CompactJourney";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  RequiresAgencyContactError,
  RequiresAgencyContactErrorSchema,
  RefundServiceQuoteResponse,
} from "@gen/travel/v1/refund_pb";
import { confirmRefund, quoteRefund } from "@services/refund";
import { useToaster } from "@hooks/useToaster";
import { ConnectError } from "@connectrpc/connect";

interface CancelModalProps {
  travel?: Travel;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CancelModal({
  travel,
  isOpen,
  onClose,
  onSuccess,
}: CancelModalProps) {
  const { t } = useTranslation();
  const { addToast } = useToaster();

  const [quote, setQuote] = useState<RefundServiceQuoteResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [error, setError] = useState<RequiresAgencyContactError>();

  async function fetchQuote() {
    if (!travel) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const res = await quoteRefund(travel.id);
      setQuote(res);
    } catch (e) {
      if (e instanceof ConnectError && e.rawMessage) {
        if (e.findDetails(RequiresAgencyContactErrorSchema).length > 0) {
          setError(e.findDetails(RequiresAgencyContactErrorSchema)[0]);
        } else if (e.rawMessage) {
          addToast("error", e.rawMessage);
        }
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirm() {
    if (!travel) return;
    setIsRefunding(true);
    try {
      await confirmRefund(travel.id);
      addToast("success", t("userBookings.sucessfulCancellation"));
      onSuccess();
    } catch (e) {
      if (e instanceof ConnectError) {
        if (e.findDetails(RequiresAgencyContactErrorSchema).length > 0) {
          setError(e.findDetails(RequiresAgencyContactErrorSchema)[0]);
        } else if (e.rawMessage) {
          addToast("error", e.rawMessage);
        }
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsRefunding(false);
    }
  }

  useEffect(() => {
    if (!isOpen) return;
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchQuote();
  }, [isOpen, travel]);

  if (!travel || !travel.outward || !travel.inward) return null;

  function content() {
    if (isLoading) {
      return <Skeleton animation height="200px" />;
    }
    if (error) {
      return <DisplayError error={error} />;
    }
    const subRow = "text-sm text-text pl-4";
    const agencyFees = formatPrice(quote?.processingFees);
    const paidAmount = formatPrice(quote?.outOfPocketAmount);

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-bold text-text-strong">
            {t("booking.travelCost")}
          </span>
          <AmountRow
            label={t("booking.initialPrice")}
            value={formatPrice(quote?.baseAmount)}
          />
          <AmountRow label={t("booking.agencyFees")} value={agencyFees} />
          <AmountRow
            label={t("booking.totalAmount")}
            value={formatPrice(quote?.totalAmount)}
            className="font-bold"
          />
          <AmountRow
            label={t("booking.coveredAmount", {
              percentage: quote?.coveragePercentage,
            })}
            value={formatPrice(quote?.coveredAmount)}
            className={subRow}
          />
          <AmountRow
            label={t("booking.paidAmount", {
              percentage: quote?.outOfPocketPercentage,
            })}
            value={paidAmount}
            className={subRow}
          />
        </div>
        {quote?.fullyCovered ? (
          <p className="text-sm text-text">
            {t("booking.noRefundFullyCovered")}
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            <span className="font-bold text-text-strong">
              {t("booking.yourRefund")}
            </span>
            <AmountRow label={t("booking.amountYouPaid")} value={paidAmount} />
            <AmountRow
              label={t("booking.yourAgencyFeesShare", {
                percentage: quote?.outOfPocketPercentage,
                fees: agencyFees,
              })}
              value={`-${formatPrice(quote?.customerAgencyFees)}`}
            />
            <div className="flex gap-2 justify-between p-2 rounded-md bg-cnam-blue-light items-center">
              <span>{t("booking.yourRefundAmount")}</span>
              <span className="text-xl font-bold">
                {formatPrice(quote?.customerRefundAmount)}
              </span>
            </div>
            <p className="text-sm text-text">
              {t("booking.refundExplanation")}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal size="small" isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <span className="font-jakarta font-bold text-text-strong">
          {`${t("booking.roundTrip")} : ${travel.outward?.route?.origin?.name} → ${travel.inward?.route?.origin?.name}`}
        </span>
        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
          <CompactJourney
            journey={travel.outward}
            travel={travel}
            displayPrice
          />
          <CompactJourney
            journey={travel.inward}
            travel={travel}
            displayPrice
          />
        </div>
        {content()}
        {!isLoading && !error && (
          <div className="flex gap-2 justify-end">
            <Button onClick={onClose} kind="outline" size="compact">
              {t("cancel")}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isRefunding}
              isLoading={isRefunding}
              size="compact"
            >
              {t("confirm")}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function AmountRow({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`flex gap-2 justify-between items-center ${className}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function DisplayError({ error }: { error?: RequiresAgencyContactError }) {
  const { t } = useTranslation();

  if (!error || !error.agency) return null;
  const agency = error.agency;
  return (
    <p className="text-neutral-900 whitespace-pre-line max-w-[600px]">
      <Trans
        t={t}
        values={{
          agency: agency.alias,
          phone: agency.treepRecapPhoneNumber,
          hours: agency.openingHours,
        }}
      >
        userBookings.errors.cancellation
      </Trans>
    </p>
  );
}
